import React, { useEffect, useState } from 'react';

import { RiAddLine, RiEditLine } from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { ColorPickerPresets } from '@/design-system/color-picker';
import { Field, FieldDescription } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { SegmentedControl, SegmentedItem } from '@/design-system/segmented-control';
import { toast } from '@/design-system/toaster';
import {
  CreateZonePayload,
  UpdateZonePayload,
  Zone,
  ZoneStatus,
  zonesAPI,
} from '@/services/api/zones';

interface ZoneFormModalProps {
  isOpen: boolean;
  /** Hub the zone belongs to (required on create). */
  hubId: number;
  /** Null = create mode; a zone = edit mode. */
  zone: Zone | null;
  onClose: () => void;
  onSaved: (zone: Zone) => void;
}

interface FormState {
  code: string;
  name: string;
  color: string;
  status: ZoneStatus;
}

const ZONE_CODE_REGEX = /^[A-Z0-9_]+$/;

// Operational data-vis palette. Deliberately no red / amber / pure orange —
// in an editor those read as "alert" or "error" and make the surface feel
// alarming when nothing's wrong. Every option here is a chromatic
// territory-marking hue.
const PALETTE: string[] = [
  '#7d52f4', // brand purple
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#fb7185', // coral (warm pink — not red)
  '#d946ef', // magenta
  '#64748b', // slate
  '#a78bfa', // plum
  '#ca8a04', // mustard
];

const EMPTY: FormState = {
  code: '',
  name: '',
  color: PALETTE[0],
  status: 'active',
};

const ZoneFormModal: React.FC<ZoneFormModalProps> = ({
  isOpen,
  hubId,
  zone,
  onClose,
  onSaved,
}) => {
  const isEdit = zone !== null;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});

  useEffect(() => {
    if (!isOpen) return;
    setIsSubmitting(false);
    setFieldErrors({});
    if (zone) {
      setForm({
        code: zone.code,
        name: zone.name,
        color: zone.color,
        status: zone.status,
      });
    } else {
      setForm(EMPTY);
    }
  }, [isOpen, zone]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {};

    if (!isEdit) {
      if (!form.code.trim()) {
        errors.code = 'Code is required';
      } else if (!ZONE_CODE_REGEX.test(form.code.trim())) {
        errors.code = 'Uppercase letters, digits, and underscores only';
      } else if (form.code.length > 64) {
        errors.code = 'Code must be 64 characters or less';
      }
    }

    if (!form.name.trim()) {
      errors.name = 'Name is required';
    } else if (form.name.trim().length > 80) {
      errors.name = 'Name must be 80 characters or less';
    }

    if (!/^#[0-9A-Fa-f]{6}$/.test(form.color)) {
      errors.color = 'Color must be #RRGGBB';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      let saved: Zone;
      if (isEdit && zone) {
        const payload: UpdateZonePayload = {
          name: form.name.trim(),
          color: form.color,
          status: form.status,
        };
        saved = await zonesAPI.update(zone.id, payload);
        toast.success('Zone updated', { description: `${saved.code} saved.` });
      } else {
        const payload: CreateZonePayload = {
          hubId,
          code: form.code.trim(),
          name: form.name.trim(),
          color: form.color,
        };
        saved = await zonesAPI.create(payload);
        toast.success('Zone created', { description: `${saved.code} added.` });
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(isEdit ? 'Could not update zone' : 'Could not create zone', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={isEdit ? <RiEditLine /> : <RiAddLine />}
      iconTone="primary"
      title={isEdit ? 'Edit zone' : 'New zone'}
      subtitle={
        isEdit && zone
          ? `${zone.code} · ${zone.name}`
          : 'A territory of H3 cells served by this hub.'
      }
      size="md"
      confirmOnClose
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting
              ? isEdit
                ? 'Saving…'
                : 'Creating…'
              : isEdit
                ? 'Save changes'
                : 'Create zone'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field>
          <Label htmlFor="zone-code" required={!isEdit}>
            Code
            {isEdit && (
              <Badge status="neutral" size="sm" className="ml-1">
                Immutable
              </Badge>
            )}
          </Label>
          <InputRoot size="md" invalid={!!fieldErrors.code}>
            <Input
              id="zone-code"
              type="text"
              value={form.code}
              disabled={isEdit}
              onChange={(e) => setField('code', e.target.value.toUpperCase())}
              placeholder="UTARA_Z1"
              className="font-mono"
            />
          </InputRoot>
          {fieldErrors.code ? (
            <Hint tone="error">{fieldErrors.code}</Hint>
          ) : (
            !isEdit && (
              <FieldDescription>
                Unique within this hub. Uppercase, digits, and underscores.
              </FieldDescription>
            )
          )}
        </Field>

        <Field>
          <Label htmlFor="zone-name" required>
            Name
          </Label>
          <InputRoot size="md" invalid={!!fieldErrors.name}>
            <Input
              id="zone-name"
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Kelapa Gading + Sunter"
              maxLength={80}
            />
          </InputRoot>
          {fieldErrors.name && <Hint tone="error">{fieldErrors.name}</Hint>}
        </Field>

        <Field>
          <Label>Color</Label>
          <div className="flex flex-wrap items-center gap-3">
            <ColorPickerPresets
              presets={PALETTE}
              value={form.color}
              onValueChange={(c) => setField('color', c)}
              className="border-t-0 pt-0"
            />
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color}
                onChange={(e) =>
                  setField('color', e.target.value.toLowerCase())
                }
                aria-label="Custom color"
                className="h-8 w-8 cursor-pointer rounded-md border border-stroke-soft-200 bg-bg-white-0"
              />
              <span className="font-mono text-[11px] tabular text-text-sub-600">
                {form.color}
              </span>
            </div>
          </div>
          {fieldErrors.color && <Hint tone="error">{fieldErrors.color}</Hint>}
        </Field>

        {isEdit && (
          <Field>
            <Label>Status</Label>
            <SegmentedControl
              value={form.status}
              onValueChange={(v: string) => {
                if (v) setField('status', v as ZoneStatus);
              }}
              className="w-full [&>*]:flex-1"
            >
              {(['active', 'inactive'] as ZoneStatus[]).map((opt) => (
                <SegmentedItem key={opt} value={opt}>
                  {opt === 'active' ? 'Active' : 'Inactive'}
                </SegmentedItem>
              ))}
            </SegmentedControl>
            <FieldDescription>
              Inactive zones stop showing in the resolver but their cells stay
              attached. Re-activate any time (hub must be active).
            </FieldDescription>
          </Field>
        )}
      </div>
    </FormModal>
  );
};

export default ZoneFormModal;
